import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Github, Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { createLogger } from "../../lib/logger";
import { useAppStore } from "../../store/useAppStore";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { DarkBackground } from "../ui/dark-background";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Logo } from "../ui/logo";

const log = createLogger("LoginForm");

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;

export function LoginForm() {
  const [error, setError] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, login } = useAppStore();

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const handleOAuthLogin = async (provider: string) => {
    try {
      setError("");
      // Get OAuth authorization URL from backend
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8003/api"
        }/auth/oauth/${provider}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate OAuth flow");
      }

      const data = await response.json();

      // Remember which provider we're authenticating with. The OAuth redirect
      // lands on /auth/callback with no provider in the path, so the callback
      // reads this back to route to the correct backend endpoint.
      sessionStorage.setItem("oauth-provider", provider);

      // Redirect to OAuth provider
      window.location.href = data.auth_url;
    } catch (err: any) {
      setError(err.message || "OAuth login failed");
    }
  };

  const handleEmailLogin = async (data: LoginFormData) => {
    try {
      setError("");

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8003/api"
        }/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const result = await response.json();

      // Store token and user data
      localStorage.setItem("avahana-token", result.token);

      // Update app store with credentials format
      await login(data);

      // SUCCESS! Reload page to trigger App re-render with new auth state
      log.debug("✅ Login successful, reloading to apply authentication...");
      window.location.replace("/projects");
    } catch (err: any) {
      log.error("❌ Login failed", err);
      setError(err.message || "Login failed");
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    try {
      setError("");
      // TODO: Implement OTP-based signup flow
      log.debug("Signup data", data);
    } catch (err: any) {
      setError(err.message || "Signup failed");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <DarkBackground />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left Column - Branding */}
        <div className="hidden lg:flex flex-col justify-center items-center p-12">
          <div className="max-w-md text-center space-y-6">
            <div className="flex justify-center mb-8">
              <Logo size="xl" />
            </div>
            <h1 className="text-display leading-tight text-foreground">
              Welcome to Āvāhana
            </h1>
            <p className="text-xl text-muted-foreground">
              Your powerful developer cloud platform for seamless deployments
            </p>
            <div className="space-y-4 mt-8">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">
                  Deploy applications in seconds
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">
                  Integrate with GitHub, GitLab & more
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">
                  Monitor and scale with ease
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Login Form */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <Card className="border-border bg-card">
              <CardHeader className="text-center">
                <div className="lg:hidden flex justify-center mb-4">
                  <Logo size="lg" />
                </div>

                {/* Tab Toggle */}
                <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setIsSignup(false)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      !isSignup
                        ? "bg-background text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsSignup(true)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      isSignup
                        ? "bg-background text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Sign up
                  </button>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {error && (
                  <div className="bg-destructive/15 border-destructive/20 text-destructive p-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {isSignup ? (
                  /* Signup Form */
                  <form
                    onSubmit={signupForm.handleSubmit(handleSignup)}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name</Label>
                        <Input
                          id="firstName"
                          placeholder="John"
                          {...signupForm.register("firstName")}
                        />
                        {signupForm.formState.errors.firstName && (
                          <p className="text-destructive text-sm">
                            {signupForm.formState.errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <Input
                          id="lastName"
                          placeholder="Doe"
                          {...signupForm.register("lastName")}
                        />
                        {signupForm.formState.errors.lastName && (
                          <p className="text-destructive text-sm">
                            {signupForm.formState.errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signupEmail">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signupEmail"
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10"
                          {...signupForm.register("email")}
                        />
                      </div>
                      {signupForm.formState.errors.email && (
                        <p className="text-destructive text-sm">
                          {signupForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(775) 351-6501"
                        {...signupForm.register("phone")}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={signupForm.formState.isSubmitting || isLoading}
                    >
                      {signupForm.formState.isSubmitting
                        ? "Creating account..."
                        : "Create an account"}
                    </Button>
                  </form>
                ) : (
                  /* Login Form */
                  <form
                    onSubmit={loginForm.handleSubmit(handleEmailLogin)}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="loginEmail">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="loginEmail"
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10"
                          {...loginForm.register("email")}
                        />
                      </div>
                      {loginForm.formState.errors.email && (
                        <p className="text-destructive text-sm">
                          {loginForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          className="pl-10 pr-10"
                          {...loginForm.register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="text-destructive text-sm">
                          {loginForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loginForm.formState.isSubmitting || isLoading}
                    >
                      {loginForm.formState.isSubmitting
                        ? "Signing in..."
                        : "Sign In"}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground text-sm underline"
                        onClick={() => {
                          /* TODO: Implement forgot password */
                        }}
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                )}

                {/* OR Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      OR SIGN IN WITH
                    </span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOAuthLogin("google")}
                    disabled={isLoading}
                    className="w-full"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Google
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOAuthLogin("github")}
                    disabled={isLoading}
                    className="w-full"
                  >
                    <Github className="mr-2 h-4 w-4" />
                    GitHub
                  </Button>
                </div>

                {/* Footer */}
                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    By creating an account, you agree to our{" "}
                    <button className="underline hover:text-foreground">
                      Terms & Service
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
